import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MoonIcon, DownloadSimpleIcon, LockKeyIcon } from "@phosphor-icons/react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function DayEndLog() {
  const [rows, setRows] = useState([]);
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (propertyId && propertyId !== "all") params.property_id = propertyId;
      if (start) params.start = start;
      if (end) params.end = end;
      const r = await api.get("/day-end/logs", { params });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load day-end logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [propertyId, start, end]);

  const totals = useMemo(() => {
    return rows.reduce((acc, r) => {
      const rev = r.snapshot?.revenue || {};
      acc.rooms += Number(rev.rooms || 0);
      acc.pos += Number(rev.pos_direct || 0) + Number(rev.pos_on_invoice || 0);
      acc.tax += Number(rev.tax || 0);
      acc.paid += Number(rev.paid_amount || 0);
      acc.no_shows += (r.no_show_ids || []).length;
      return acc;
    }, { rooms: 0, pos: 0, tax: 0, paid: 0, no_shows: 0 });
  }, [rows]);

  const exportCsv = () => {
    const headers = ["Date", "Property", "Closed by", "Closed at", "Arrivals", "Departures", "In-house", "No-shows", "Occupancy %", "Rooms revenue", "POS total", "Tax", "Paid", "Cash", "UPI", "Card", "Bank", "Travels", "Other", "Notes"];
    const lines = [headers.join(",")];
    rows.forEach((r) => {
      const c = r.snapshot?.counts || {};
      const rev = r.snapshot?.revenue || {};
      const col = r.snapshot?.collections || {};
      const row = [
        r.date,
        r.property_name || r.property_id,
        r.closed_by || "",
        (r.closed_at || "").replace("T", " ").slice(0, 16),
        c.arrivals || 0, c.departures || 0, c.in_house || 0, c.no_shows || 0, c.occupancy_pct || 0,
        rev.rooms || 0, Number(rev.pos_direct || 0) + Number(rev.pos_on_invoice || 0), rev.tax || 0, rev.paid_amount || 0,
        col.cash || 0, col.upi || 0, col.card || 0, col.bank || 0, col.travels || 0, col.other || 0,
        `"${(r.notes || "").replace(/"/g, '""')}"`,
      ];
      lines.push(row.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `day-end-log-${start || "all"}-${end || "all"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="day-end-log-panel">
      <Card className="p-6">
        <div className="flex items-center gap-3">
          <MoonIcon size={22} weight="duotone" className="text-primary" />
          <div>
            <p className="label-overline">Audit trail</p>
            <h3 className="font-display text-2xl">Day End Log</h3>
            <p className="text-sm text-muted-foreground mt-1">A read-only record of every day-end closure across properties. Snapshots the day's counts, revenue and collections at the moment of close so the audit stays truthful.</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Property</Label>
            <Select value={propertyId} onValueChange={setPropertyId}>
              <SelectTrigger className="mt-1" data-testid="day-end-log-property">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All properties</SelectItem>
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">From</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1" data-testid="day-end-log-from" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">To</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1" data-testid="day-end-log-to" />
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={exportCsv} disabled={!rows.length} data-testid="day-end-log-export">
              <DownloadSimpleIcon size={14} className="mr-1" /> Export CSV
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="day-end-log-totals">
        <StatCard label="Days closed" value={rows.length} />
        <StatCard label="Rooms revenue" value={inr(totals.rooms)} />
        <StatCard label="POS total" value={inr(totals.pos)} />
        <StatCard label="Tax collected" value={inr(totals.tax)} />
        <StatCard label="Paid" value={inr(totals.paid)} accent />
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Property</TableHead>
              <TableHead>Closed by</TableHead>
              <TableHead>Closed at</TableHead>
              <TableHead className="text-right">Arrivals</TableHead>
              <TableHead className="text-right">Deps.</TableHead>
              <TableHead className="text-right">In-house</TableHead>
              <TableHead className="text-right">No-shows</TableHead>
              <TableHead className="text-right">Occ %</TableHead>
              <TableHead className="text-right">Rooms ₹</TableHead>
              <TableHead className="text-right">POS ₹</TableHead>
              <TableHead className="text-right">Paid ₹</TableHead>
              <TableHead>Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const c = r.snapshot?.counts || {};
              const rev = r.snapshot?.revenue || {};
              return (
                <TableRow key={r.id} data-testid={`day-end-log-row-${r.date}-${r.property_id}`}>
                  <TableCell className="font-mono-plex text-xs">{r.date}</TableCell>
                  <TableCell>{r.property_name || "—"}</TableCell>
                  <TableCell className="text-xs">
                    <div className="flex items-center gap-1"><LockKeyIcon size={11} weight="fill" className="text-primary" />{r.closed_by || "—"}</div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono-plex">{(r.closed_at || "").replace("T", " ").slice(0, 16)}</TableCell>
                  <TableCell className="text-right">{c.arrivals ?? "—"}</TableCell>
                  <TableCell className="text-right">{c.departures ?? "—"}</TableCell>
                  <TableCell className="text-right">{c.in_house ?? "—"}</TableCell>
                  <TableCell className="text-right">
                    {c.no_shows > 0 ? <Badge variant="outline" className="bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]">{c.no_shows}</Badge> : (c.no_shows ?? "—")}
                  </TableCell>
                  <TableCell className="text-right font-mono-plex">{c.occupancy_pct ?? "—"}</TableCell>
                  <TableCell className="text-right font-mono-plex">{inr(rev.rooms)}</TableCell>
                  <TableCell className="text-right font-mono-plex">{inr(Number(rev.pos_direct || 0) + Number(rev.pos_on_invoice || 0))}</TableCell>
                  <TableCell className="text-right font-mono-plex text-[hsl(var(--success))]">{inr(rev.paid_amount)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate" title={r.notes || ""}>{r.notes || "—"}</TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={13} className="text-center py-10 text-muted-foreground italic">
                  {loading ? "Loading…" : "No day-end closures recorded for this filter."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className={`font-display text-xl mt-1 ${accent ? "text-primary" : ""}`}>{value}</p>
    </div>
  );
}
